import React, { useState, useEffect, useCallback } from 'react';
import { auditAPI } from '../services/api';
import toast from 'react-hot-toast';
import { FiFilter, FiRefreshCw, FiTrash2, FiChevronLeft, FiChevronRight, FiAlertCircle, FiInfo, FiAlertTriangle } from 'react-icons/fi';

const SEV_CFG = {
  info:    { bg:'#eff6ff', text:'#1d4ed8', dot:'#3b82f6', icon:<FiInfo size={11}/>,           label:'Info' },
  warning: { bg:'#fef3c7', text:'#92400e', dot:'#f59e0b', icon:<FiAlertTriangle size={11}/>,   label:'Warning' },
  error:   { bg:'#fee2e2', text:'#991b1b', dot:'#ef4444', icon:<FiAlertCircle size={11}/>,    label:'Error' },
};

const MODULES = ['auth','projects','tasks','attendance','leaves','salary','invoices','expenses','users','system'];

const INP = {
  width:'100%', padding:'9px 13px', border:'1.5px solid #e2e8f0', borderRadius:10,
  fontSize:13, outline:'none', boxSizing:'border-box', background:'#fafbfc',
  fontFamily:"'DM Sans',sans-serif"
};

export default function AuditLog() {
  const [logs,     setLogs]     = useState([]);
  const [stats,    setStats]    = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [total,    setTotal]    = useState(0);
  const [page,     setPage]     = useState(1);
  const [pages,    setPages]    = useState(1);
  const [filters,  setFilters]  = useState({ module:'', severity:'', userId:'', startDate:'', endDate:'' });
  const [showFilters, setShowFilters] = useState(false);
  const [showClear, setShowClear] = useState(false);
  const [clearDays, setClearDays] = useState(90);

  const LIMIT = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await auditAPI.getLogs({ ...filters, page, limit: LIMIT });
      setLogs(r.data.logs || []);
      setTotal(r.data.total || 0);
      setPages(r.data.pages || 1);
    } catch { toast.error('Failed to load audit logs'); }
    setLoading(false);
  }, [filters, page]);

  const loadStats = async () => {
    try { const r = await auditAPI.getStats(); setStats(r.data); }
    catch { /* non-critical */ }
  };

  useEffect(() => { load(); loadStats(); }, [load]);

  const handleClear = async () => {
    try {
      const r = await auditAPI.clearOld({ days: clearDays });
      toast.success(r.data.message || 'Logs cleared');
      setShowClear(false); load(); loadStats();
    } catch(err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const SevBadge = ({ sev }) => {
    const c = SEV_CFG[sev] || SEV_CFG.info;
    return (
      <span style={{ display:'inline-flex', alignItems:'center', gap:4, background:c.bg, color:c.text, padding:'3px 9px', borderRadius:20, fontSize:11, fontWeight:700 }}>
        {c.icon} {c.label}
      </span>
    );
  };

  const fmtTime = (d) => {
    if (!d) return '—';
    const dt = new Date(d);
    return dt.toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) + ' ' +
           dt.toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'});
  };

  return (
    <div style={{ padding:'28px 32px', background:'#f8fafc', minHeight:'100vh', fontFamily:"'DM Sans',sans-serif" }}>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:28 }}>
        <div>
          <h1 style={{ margin:0, fontSize:26, fontWeight:700, color:'#1e293b' }}>Audit Log</h1>
          <p style={{ margin:'4px 0 0', color:'#64748b', fontSize:14 }}>System-wide activity trail — {total.toLocaleString()} entries</p>
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <button onClick={() => setShowFilters(v=>!v)} style={{ display:'flex', alignItems:'center', gap:6, padding:'9px 16px', borderRadius:10, border:'1.5px solid #e2e8f0', background:'#fff', cursor:'pointer', fontSize:14, color:'#475569' }}>
            <FiFilter size={15}/> Filters
          </button>
          <button onClick={() => { load(); loadStats(); }} style={{ padding:'9px 14px', borderRadius:10, border:'1.5px solid #e2e8f0', background:'#fff', cursor:'pointer', color:'#475569' }}>
            <FiRefreshCw size={15}/>
          </button>
          <button onClick={() => setShowClear(true)} style={{ display:'flex', alignItems:'center', gap:6, padding:'9px 16px', borderRadius:10, border:'1.5px solid #fca5a5', background:'#fee2e2', color:'#dc2626', cursor:'pointer', fontSize:14, fontWeight:600 }}>
            <FiTrash2 size={14}/> Clear Old
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16, marginBottom:24 }}>
          {[
            { label:'Total Events', value: stats.total?.toLocaleString() || '0', icon:'📋', color:'var(--pm-primary)', bg:'#eef2ff' },
            { label:'Errors (24h)', value: stats.recentErrors || 0, icon:'🔴', color:'#ef4444', bg:'#fee2e2' },
            { label:'Top Module',   value: stats.byModule?.[0]?.module || '—', icon:'📦', color:'#0ea5e9', bg:'#e0f2fe' },
            { label:'Warnings',     value: (stats.bySeverity?.find(s=>s.severity==='warning')?.count) || 0, icon:'⚠️', color:'#f59e0b', bg:'#fef3c7' },
          ].map(s => (
            <div key={s.label} style={{ background:'#fff', borderRadius:14, padding:'16px 18px', boxShadow:'0 1px 4px #0000000d', display:'flex', alignItems:'center', gap:14 }}>
              <div style={{ width:40, height:40, borderRadius:10, background:s.bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:18 }}>{s.icon}</div>
              <div>
                <div style={{ fontSize:18, fontWeight:700, color:s.color }}>{s.value}</div>
                <div style={{ fontSize:11, color:'#94a3b8', marginTop:2 }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      {showFilters && (
        <div style={{ background:'#fff', borderRadius:14, padding:'16px 20px', marginBottom:20, boxShadow:'0 1px 4px #0000000d', display:'flex', gap:12, flexWrap:'wrap', alignItems:'flex-end' }}>
          <div style={{ flex:1, minWidth:140 }}>
            <label style={{ fontSize:11, fontWeight:700, color:'#64748b', display:'block', marginBottom:5, textTransform:'uppercase' }}>Module</label>
            <select value={filters.module} onChange={e=>{setFilters(f=>({...f,module:e.target.value}));setPage(1);}} style={{ ...INP, cursor:'pointer' }}>
              <option value="">All Modules</option>
              {MODULES.map(m=><option key={m} value={m}>{m.charAt(0).toUpperCase()+m.slice(1)}</option>)}
            </select>
          </div>
          <div style={{ flex:1, minWidth:130 }}>
            <label style={{ fontSize:11, fontWeight:700, color:'#64748b', display:'block', marginBottom:5, textTransform:'uppercase' }}>Severity</label>
            <select value={filters.severity} onChange={e=>{setFilters(f=>({...f,severity:e.target.value}));setPage(1);}} style={{ ...INP, cursor:'pointer' }}>
              <option value="">All</option>
              <option value="info">Info</option>
              <option value="warning">Warning</option>
              <option value="error">Error</option>
            </select>
          </div>
          <div style={{ flex:1, minWidth:130 }}>
            <label style={{ fontSize:11, fontWeight:700, color:'#64748b', display:'block', marginBottom:5, textTransform:'uppercase' }}>From</label>
            <input type="date" value={filters.startDate} onChange={e=>{setFilters(f=>({...f,startDate:e.target.value}));setPage(1);}} style={INP}/>
          </div>
          <div style={{ flex:1, minWidth:130 }}>
            <label style={{ fontSize:11, fontWeight:700, color:'#64748b', display:'block', marginBottom:5, textTransform:'uppercase' }}>To</label>
            <input type="date" value={filters.endDate} onChange={e=>{setFilters(f=>({...f,endDate:e.target.value}));setPage(1);}} style={INP}/>
          </div>
          <button onClick={()=>{setFilters({module:'',severity:'',userId:'',startDate:'',endDate:''});setPage(1);}} style={{ padding:'9px 14px', borderRadius:10, border:'1.5px solid #e2e8f0', background:'#fff', cursor:'pointer', fontSize:13, color:'#64748b', whiteSpace:'nowrap' }}>
            Reset
          </button>
        </div>
      )}

      {/* Log Table */}
      <div style={{ background:'#fff', borderRadius:16, boxShadow:'0 1px 4px #0000000d', overflow:'hidden' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr style={{ background:'#f8fafc', borderBottom:'2px solid #f1f5f9' }}>
              {['Timestamp','Action','Module','User','Description','Severity','IP'].map(h=>(
                <th key={h} style={{ padding:'12px 14px', textAlign:'left', fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'.5px', whiteSpace:'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ padding:48, textAlign:'center', color:'#94a3b8' }}>Loading…</td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={7} style={{ padding:48, textAlign:'center', color:'#94a3b8' }}>
                <div style={{ fontSize:40, marginBottom:12 }}>🔍</div>
                <div style={{ fontWeight:600 }}>No audit logs found</div>
              </td></tr>
            ) : logs.map((l, idx) => (
              <tr key={l._id || l.id || idx} style={{ borderBottom:'1px solid #f1f5f9' }}
                onMouseEnter={ev=>ev.currentTarget.style.background='#f8fafc'}
                onMouseLeave={ev=>ev.currentTarget.style.background='#fff'}>
                <td style={{ padding:'11px 14px', fontSize:12, color:'#64748b', whiteSpace:'nowrap' }}>{fmtTime(l.createdAt)}</td>
                <td style={{ padding:'11px 14px' }}>
                  <code style={{ background:'#f1f5f9', color:'#334155', padding:'2px 7px', borderRadius:5, fontSize:12, fontWeight:600 }}>{l.action}</code>
                </td>
                <td style={{ padding:'11px 14px' }}>
                  <span style={{ background:'#e0f2fe', color:'#0369a1', padding:'2px 8px', borderRadius:8, fontSize:11, fontWeight:600 }}>
                    {l.module}
                  </span>
                </td>
                <td style={{ padding:'11px 14px', fontSize:13, color:'#475569' }}>
                  {l.user ? (
                    <div>
                      <div style={{ fontWeight:600, color:'#1e293b', fontSize:13 }}>{l.user.name}</div>
                      <div style={{ fontSize:11, color:'#94a3b8' }}>{l.user.email}</div>
                    </div>
                  ) : <span style={{ color:'#94a3b8', fontSize:12 }}>System</span>}
                </td>
                <td style={{ padding:'11px 14px', fontSize:13, color:'#475569', maxWidth:280 }}>
                  <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={l.description}>{l.description || '—'}</div>
                  {l.entityType && l.entityId && (
                    <div style={{ fontSize:11, color:'#94a3b8', marginTop:2 }}>{l.entityType} #{l.entityId}</div>
                  )}
                </td>
                <td style={{ padding:'11px 14px' }}><SevBadge sev={l.severity}/></td>
                <td style={{ padding:'11px 14px', fontSize:12, color:'#94a3b8', fontFamily:'monospace' }}>{l.ipAddress || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        {pages > 1 && (
          <div style={{ padding:'14px 20px', borderTop:'1px solid #f1f5f9', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span style={{ fontSize:13, color:'#64748b' }}>Showing {((page-1)*LIMIT)+1}–{Math.min(page*LIMIT,total)} of {total.toLocaleString()} entries</span>
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1}
                style={{ padding:'6px 12px', borderRadius:8, border:'1.5px solid #e2e8f0', background: page===1?'#f8fafc':'#fff', color: page===1?'#cbd5e1':'#475569', cursor:page===1?'not-allowed':'pointer', display:'flex', alignItems:'center', gap:4 }}>
                <FiChevronLeft size={14}/> Prev
              </button>
              <span style={{ padding:'6px 14px', borderRadius:8, background:'var(--pm-primary)', color:'#fff', fontSize:14, fontWeight:700 }}>{page}</span>
              <button onClick={()=>setPage(p=>Math.min(pages,p+1))} disabled={page===pages}
                style={{ padding:'6px 12px', borderRadius:8, border:'1.5px solid #e2e8f0', background: page===pages?'#f8fafc':'#fff', color: page===pages?'#cbd5e1':'#475569', cursor:page===pages?'not-allowed':'pointer', display:'flex', alignItems:'center', gap:4 }}>
                Next <FiChevronRight size={14}/>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Clear Old Logs Modal */}
      {showClear && (
        <div style={{ position:'fixed', inset:0, background:'#0006', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div style={{ background:'#fff', borderRadius:16, padding:'28px', width:380, boxShadow:'0 20px 60px #0000003a' }}>
            <h3 style={{ margin:'0 0 10px', color:'#1e293b' }}>🗑️ Clear Old Audit Logs</h3>
            <p style={{ margin:'0 0 18px', color:'#64748b', fontSize:14 }}>Delete all logs older than the specified number of days. This cannot be undone.</p>
            <label style={{ fontSize:13, fontWeight:600, color:'#475569', display:'block', marginBottom:8 }}>Days to keep</label>
            <input type="number" min="7" max="365" value={clearDays} onChange={e=>setClearDays(e.target.value)} style={{ ...INP, marginBottom:20 }}/>
            <div style={{ display:'flex', gap:12 }}>
              <button onClick={()=>setShowClear(false)} style={{ flex:1, padding:'10px', borderRadius:10, border:'1.5px solid #e2e8f0', background:'#fff', cursor:'pointer', fontSize:14, color:'#64748b', fontWeight:600 }}>Cancel</button>
              <button onClick={handleClear} style={{ flex:1, padding:'10px', borderRadius:10, border:'none', background:'#ef4444', color:'#fff', cursor:'pointer', fontSize:14, fontWeight:700 }}>Delete Logs</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
